import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTable, { type Column } from '@/components/base/DataTable';
import { PriorityBadge, StatusBadge } from '@/components/feature/StatusBadge';
import { useChangedRowHighlight } from '@/hooks/useChangedRowHighlight';
import { cn } from '@/lib/cn';
import { formatCurrency } from '@/lib/format';
import type { OrderView } from '@/types';

interface OrdersTableProps {
  orders: OrderView[];
  currency: string;
  /** Active list tab, so returning from a detail keeps the same tab. */
  returnTab?: string;
}

function detailsPath(id: string, returnTab?: string): string {
  return returnTab && returnTab !== 'new'
    ? `/dashboard/orders/${id}?tab=${returnTab}`
    : `/dashboard/orders/${id}`;
}

function ViewLink({ id, returnTab }: { id: string; returnTab?: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={detailsPath(id, returnTab)}
      className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
    >
      <i className="ri-eye-line text-base" aria-hidden="true" />
      {t('common.viewDetails')}
    </Link>
  );
}

export default function OrdersTable({ orders, currency, returnTab }: OrdersTableProps) {
  const { t } = useTranslation();

  const highlighted = useChangedRowHighlight(
    orders,
    (order) => order.id,
    (order) => order.status,
    'dashboard-orders',
  );

  const columns: Column<OrderView>[] = [
    {
      key: 'id',
      header: t('orders.col.order'),
      render: (o) => (
        <span className="lb-ltr-field whitespace-nowrap font-semibold text-foreground-950">{o.id}</span>
      ),
    },
    {
      key: 'customer',
      header: t('orders.col.customer'),
      render: (o) => <span className="font-medium text-foreground-900">{o.customerName}</span>,
    },
    {
      key: 'priority',
      header: t('orders.col.priority'),
      render: (o) => <PriorityBadge priority={o.priority} />,
    },
    {
      key: 'fulfillment',
      header: t('orders.col.fulfillment'),
      render: (o) => <span className="text-foreground-700">{t(`fulfillment.${o.fulfillment}`)}</span>,
    },
    {
      key: 'total',
      header: t('orders.col.total'),
      render: (o) => (
        <span className="whitespace-nowrap font-semibold text-foreground-900">
          {formatCurrency(o.total, currency)}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('orders.col.status'),
      render: (o) => <StatusBadge status={o.status} />,
    },
    {
      key: 'actions',
      header: t('orders.col.actions'),
      className: 'text-end',
      render: (o) => (
        <div className="flex justify-end">
          <ViewLink id={o.id} returnTab={returnTab} />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="hidden md:block">
        <DataTable
          columns={columns}
          rows={orders}
          rowKey={(o) => o.id}
          rowClassName={(o) => (highlighted.has(o.id) ? 'lb-row-flash' : '')}
        />
      </div>

      <ul className="divide-y divide-background-100 md:hidden">
        {orders.map((o) => (
          <li key={o.id} className={cn('space-y-3 p-4', highlighted.has(o.id) && 'lb-row-flash')}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="lb-ltr-field text-sm font-semibold text-foreground-950">{o.id}</p>
                <p className="mt-0.5 truncate text-sm font-medium text-foreground-800">{o.customerName}</p>
              </div>
              <StatusBadge status={o.status} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <PriorityBadge priority={o.priority} />
              <span className="text-xs text-foreground-500">{t(`fulfillment.${o.fulfillment}`)}</span>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-background-100 pt-3">
              <span className="whitespace-nowrap text-sm font-semibold text-foreground-900">
                {formatCurrency(o.total, currency)}
              </span>
              <ViewLink id={o.id} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}